// src/components/video/VideoPlayer/VideoPlayer.jsx
import { useEffect, useRef } from "react";
import Hls from "hls.js";
import styles from "./VideoPlayer.module.scss";

const DEFAULT_API_BASE = import.meta?.env?.VITE_API_BASE_URL || "/api";

export default function VideoPlayer({
  token,
  apiBase = DEFAULT_API_BASE,
  videoId,
  hlsMasterUrl,
  mode = "cloudfront", // "cloudfront" | "proxy"
  refreshSecs = 240,
  autoPlay = false,
  muted = true,
  controls = true,
  poster,
  captions,
  className,
  style,
}) {
  const videoRef = useRef(null);
  const timerRef = useRef(null);
  const hlsRef = useRef(null);
  const unlockOrientationRef = useRef(() => {});

  async function authorize() {
    if (mode !== "cloudfront" || !videoId) return { hlsUrl: null };

    const res = await fetch(`${apiBase}/v1/videos/${videoId}/authorize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
      credentials: "include",
    });

    if (!res.ok) throw new Error(`authorize failed: ${res.status}`);

    const {
      cookies,
      domain,
      path,
      expiresAtSec,
      hlsMasterUrl: fromServer,
    } = await res.json();

    const expStr = new Date(expiresAtSec * 1000).toUTCString();

    for (const [name, value] of Object.entries(cookies || {})) {
      document.cookie = `${name}=${value}; Domain=${domain}; Path=${
        path || "/"
      }; Expires=${expStr}; Secure; SameSite=None`;
    }

    return { hlsUrl: fromServer || null };
  }

  function withAccessToken(url) {
    if (!url || !token || mode !== "proxy") return url;
    try {
      const u = new URL(url, window.location.origin);
      u.searchParams.set("access_token", token);
      return u.toString();
    } catch {
      return url;
    }
  }

  function attachHls(srcUrl) {
    const video = videoRef.current;
    if (!video || !srcUrl) return;

    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {
        // ignore
      }
      hlsRef.current = null;
    }

    const canNativeHls =
      mode === "cloudfront" &&
      video.canPlayType("application/vnd.apple.mpegurl");

    // Safari / iOS can play HLS natively
    if (canNativeHls) {
      video.src = srcUrl;
      return;
    }

    // Fallback: plain src if Hls.js is not supported
    if (!Hls.isSupported()) {
      video.src = srcUrl;
      return;
    }

    const hls = new Hls({
      enableWorker: true,
      autoStartLoad: true,
      fetchSetup: (_ctx, init) => {
        if (mode === "proxy") {
          return { ...init, credentials: "omit" };
        }
        if (mode === "cloudfront") {
          return { ...init, credentials: "include" };
        }
        return init || {};
      },
      xhrSetup: (xhr) => {
        if (mode === "cloudfront") {
          xhr.withCredentials = true;
        }
      },
    });

    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data?.fatal) return;
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;
        default:
          try {
            hls.destroy();
          } catch {
            // ignore
          }
          attachHls(srcUrl);
      }
    });

    hls.loadSource(withAccessToken(srcUrl));
    hls.attachMedia(video);
    hlsRef.current = hls;
  }

  // Init HLS / source + refresh CloudFront cookies
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        let src =
          mode === "proxy"
            ? `${apiBase}/v1/videos/${videoId}/hls/master.m3u8`
            : hlsMasterUrl || "";

        if (mode === "cloudfront" && videoId) {
          const { hlsUrl } = await authorize();
          if (hlsUrl) src = hlsUrl;
        }

        if (!mounted) return;
        if (!src) throw new Error("No HLS master URL available");

        attachHls(src);

        if (mode === "cloudfront" && videoId) {
          timerRef.current = setInterval(
            () => {
              authorize().catch((e) => console.error("[authorize]", e));
            },
            Math.max(60, refreshSecs) * 1000,
          );
        }
      } catch (e) {
        console.error("[VideoPlayer init]", e);
      }
    })();

    return () => {
      mounted = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {
          // ignore
        }
        hlsRef.current = null;
      }
      unlockOrientationRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, mode, hlsMasterUrl, apiBase, token]);

  // Some Android browsers can leave the SPA "stuck" in landscape after a
  // fullscreen video exit if the orientation lock is not cleared. Mirror the
  // Admin page behaviour by explicitly unlocking on fullscreen change.
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;

    const tryUnlock = () => {
      if (typeof screen === "object" && screen.orientation?.unlock) {
        try {
          screen.orientation.unlock();
        } catch (e) {
          console.warn("[VideoPlayer] unlock orientation failed", e);
        }
      }
    };

    unlockOrientationRef.current = tryUnlock;

    const handleFsChange = () => {
      // When exiting fullscreen, clear any lingering landscape lock.
      if (!document.fullscreenElement) {
        tryUnlock();
      }
    };

    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
    };
  }, []);

  // Auto pause when card goes off screen (nice for feed UX)
  useEffect(() => {
    const container = videoRef.current?.closest(`.${styles.container}`);
    const v = videoRef.current;
    if (!container || !v) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            v.pause();
          }
        });
      },
      { threshold: 0.4 },
    );

    io.observe(container);
    return () => io.disconnect();
  }, []);

  const controlsListValue = [
    "nodownload",
    "noremoteplayback",
    "noplaybackrate",
  ].join(" ");

  return (
    <div
      className={[styles.container, className || ""].join(" ")}
      style={style}
    >
      <div className={styles.frame}>
        <video
          ref={videoRef}
          className={styles.video}
          controls={controls}
          controlsList={controlsListValue}
          playsInline
          autoPlay={autoPlay}
          muted={muted}
          poster={poster}
        >
          {captions &&
            Object.entries(captions).map(([lang, url]) => (
              <track
                key={lang}
                label={lang.toUpperCase()}
                kind="subtitles"
                srcLang={lang}
                src={url}
              />
            ))}
        </video>
      </div>
    </div>
  );
}
